import { useMemo } from "react";

import type { Auth } from "./application/domain/auth/auth";
import envConfig from "./utils/env-config";

// Feature Flag =========================================================================================================
/**
 * 開発の過程などで定義され、最終的に削除される想定のフラグ
 * 特定テナントや手続き等、環境以外の変数で機能提供を制御する場合は useFeatureFlags_XXX, featureFlags_XXX という形で追加する
 */
export const FeatureFlags = {
  // フォーム画像項目機能の制御
  // ref:
  //   https://docs.google.com/document/d/14U5Oa87BpgVtm7OfflSVrOyyqPqe8Y4EUVtkvhXZ0uM/edit?tab=t.0#heading=h.6392lzo8gr4n
  ENABLE_GRAFFER_SCHEMAS_STATIC_IMAGE: envConfig.matchEnvValue({
    local: false,
    dev: false,
    staging: false,
    staging_sandbox: false,
    production: false,
    production_sandbox: false,
  }),
};

// Service Provider 画面でのデータ削除を可能とする
// https://docs.google.com/document/d/1j78l_w5OqBE5lnDdfQwGyHJzbV_PyP2cxnxa7EpdrnA/edit?tab=t.0#heading=h.iwvofh84bm2t
export const useFeatureFlag_EnableProductDataRemovalTasks = (orgId: string) => {
  return useMemo(() => {
    return envConfig.matchEnvValue({
      local: true,
      dev: true,
      staging: true,
      staging_sandbox: true,
      production: orgId === "8509814050915372047", // 水俣市
      production_sandbox: orgId === "0264384975919858262", // 水俣市
    });
  }, [orgId]);
};

// 申請振り分け機能の制御
// useFeatureFlag_EnableAssignmentSettingRuleset にかかる機能実装が完了次第同時に削除される想定
// ref:
//   https://docs.google.com/document/d/1Kcgvt0anZ2JfJYHAIo_wsLlTK2dSMU8boWebJsoar68/edit?tab=t.0#heading=h.joelxhqjyz09
export const useFeatureFlag_EnableApplyAssignment = (orgSlug: string) => {
  return useMemo(() => {
    return envConfig.matchEnvValue({
      local: orgSlug === "tfd-metro-tokyo" || orgSlug === "city-graffer",
      dev: false,
      staging: orgSlug === "tfd-metro-tokyo" || orgSlug === "city-graffer",
      staging_sandbox: false,
      production: orgSlug === "tfd-metro-tokyo" || orgSlug === "city-graffer",
      production_sandbox: orgSlug === "tfd-metro-tokyo",
    });
  }, [orgSlug]);
};

// 申請振り分け機能汎用化に向けた設定画面機能の制御
// FeatureFlag_EnableApplyAssignment は既に tfd-metro-tokyo に開放されているが、
// テナント側の振り分け設定画面の機能化が完了するまでは、別管理したいために作成したフラグ
// ref:
//   https://docs.google.com/document/d/1JROpsTtwXJ05gII3t4BP_h7_8LAiH0EvDGo8WvSLr60/preview?tab=t.0#heading=h.joelxhqjyz09
export const useFeatureFlag_EnableAssignmentSettingRuleset = () => {
  return useMemo(() => {
    return envConfig.matchEnvValue({
      local: true,
      dev: true,
      staging: false,
      staging_sandbox: false,
      production: false,
      production_sandbox: false,
    });
  }, []);
};

// グループごとにACL設定を無視する機能の制御
// city-nagoya向け暫定対応である(名古屋市との契約上、この制御方法が必須だったという経緯がある)
// city-nagoya以外で積極的な要望がないこと、IP設定状況が複雑になるなどの理由で、仮に要望があれば当該機能はなくなる前提でよりより制御方法を検討する想定
// ref:
//  https://docs.google.com/document/d/1jXMlEwfI9dTqvQWTPFpQTEhjI7wWo7T8a6WNbw5mJlg/edit?tab=t.0#heading=h.joelxhqjyz09
export const useFeatureFlag_IgnoreAclSettingByGroup = (orgSlug: string) => {
  const result = useMemo(() => {
    return ["city-graffer", "city-graffer-ci", "city-nagoya"].includes(orgSlug);
  }, [orgSlug]);
  return result;
};

// 市民側の帳票PDFダウンロード機能の制御
// PDFマッピングなどのエラーが発生した場合、テナント側での帳票設定の修正が必須であり、仮に全体解放を進めていく上では運用を踏まえた整理が必要
// ref:
//  https://docs.google.com/document/d/1pEMJE8nTqI-8T0vZLyk5tnkDmyHVuZcJQOlfCiRrwNM/edit?tab=t.0#heading=h.joelxhqjyz09
export const useFeatureFlag_CustomerDlConfig = (orgId: string): boolean => {
  const result = useMemo(() => {
    const enabledEnvOrgIdMap: Record<string, Array<string>> = {
      staging: [
        "7448845465393709659", // city-graffer
      ],
      staging_sandbox: [
        "2383609903543766469", // city-graffer
      ],
      production: [
        "2075642586519060485", // city-graffer
        "1792867455648550033", // city-fukuoka
        "4275495840575930468", // tachikawa-kyusyoku
        "7409784218367444651", // tokyoengagement
        "7051271020789384208", // mitaka-kyusyoku
        "6509010177442989989", // tokyo-sg
      ],
      production_sandbox: [
        "8973410915131343407", // city-graffer
        "7538920256699516344", // city-fukuoka
        "8115128490352330133", // tachikawa-kyusyoku
        "4539184212184024747", // tokyoengagement
        "3814565130898263166", // mitaka-kyusyoku
        "8627933714995366445", // tokyo-sg
      ],
    };

    return envConfig.matchEnvValue({
      local: true,
      dev: true,
      staging: enabledEnvOrgIdMap["staging"].includes(orgId),
      staging_sandbox: enabledEnvOrgIdMap["staging_sandbox"].includes(orgId),
      production: enabledEnvOrgIdMap["production"].includes(orgId),
      production_sandbox:
        enabledEnvOrgIdMap["production_sandbox"].includes(orgId),
    });
  }, [orgId]);
  return result;
};

// 職責署名検証機能の制御
// ref:
//  https://docs.google.com/document/d/1YMy1QzXXec0_okMB-ucHUocNQsdWD3CqmzBZHgg9gFI/edit?tab=t.0#heading=h.joelxhqjyz09
//  https://graffer.esa.io/posts/4066
export const featureFlag_EnableLgpkiSignature = (auth: Auth) => {
  return envConfig.matchEnvValue({
    local: true,
    dev: true,
    staging: auth.data && ["city-graffer"].includes(auth.data.orgSlug),
    staging_sandbox: false,
    production:
      auth.data && ["city-graffer", "city-fukuoka"].includes(auth.data.orgSlug),
    production_sandbox:
      auth.data && ["city-graffer", "city-fukuoka"].includes(auth.data.orgSlug),
  });
};

// StaticFeatureFlags ====================================================================================================
/**
 * 削除される予定のないフラグ
 * 主に開発の制約や機能の立て付けとして特定の環境へ意図的に提供しない場合などに利用する
 * 機能リリースに関連するフラグはFeatureFlagsにて管理する
 */
export const StaticFeatureFlags = {
  IS_SANDBOX: envConfig.matchEnvValue({
    local: false,
    dev: false,
    production: false,
    production_sandbox: true,
    staging: false,
    staging_sandbox: true,
  }),
  // 開発時の Maximum update depth exceeded エラー検知
  // ref:
  //  https://github.com/graffer-inc/cloud-gov/pull/16413
  ENABLE_SUBTLE_ERROR_DETECTOR: envConfig.matchEnvValue({
    local: true,
    dev: false,
    staging: false,
    staging_sandbox: false,
    production: false,
    production_sandbox: false,
  }),
  // 集計機能制御, 現状はsandboxに提供する予定はない
  // ref:
  //  https://drive.google.com/drive/folders/1pFBcFcD9fEQIkOaZhgTYW65mGR7GHm68
  ENABLE_DATA_SIGHT: envConfig.matchEnvValue({
    local: true,
    dev: true,
    production: true,
    production_sandbox: false,
    staging: true,
    staging_sandbox: false,
  }),
  // Kintone連携機能制御, 現状はsandboxに提供する予定はない
  // ref:
  //  https://docs.google.com/document/d/14o-lEmtEEfMsSeeGeKVCMmILpIEO1KHeQw0mRVZACT8/edit?tab=t.0#heading=h.1ohpc999qsjq
  ENABLE_KINTONE_EXPORT: envConfig.matchEnvValue({
    local: true,
    dev: true,
    staging: true,
    staging_sandbox: false,
    production: true,
    production_sandbox: false,
  }),
  // 外部向けAPIの仕様書表示制御
  // 現状はHTMLを出力して直接データとして渡す運用となっている
  ENABLE_SMART_APPLY_EXTERNAL_API_SPEC: envConfig.matchEnvValue({
    local: false,
    dev: true,
    staging: true,
    staging_sandbox: true,
    production: false,
    production_sandbox: false,
  }),
};

// ISO27017取得の監査対応時に必要(次回以降の監査でも必要になる見込み)
// 「Graffer Platformコンソールのメニューから管理画面に遷移できること」を満たすためこのメニューを足す
// ref:
//   https://github.com/graffer-inc/cloud-gov/pull/13935
//   https://graffer.slack.com/archives/CRHQZCMDF/p1729062701122199
export const staticFeatureFlag_EnableTtzkGuideMenu = (auth: Auth) => {
  const enabledSlug = ["city-graffer"];

  return envConfig.matchEnvValue({
    local: !!auth.data && enabledSlug.includes(auth.data.orgSlug),
    dev: !!auth.data && enabledSlug.includes(auth.data.orgSlug),
    staging: !!auth.data && enabledSlug.includes(auth.data.orgSlug),
    staging_sandbox: false,
    production: false,
    production_sandbox: false,
  });
};
